import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Form,
  FormGroup,
  FormFeedback,
  Button,
  Input,
  Label,
  Alert,
} from 'reactstrap';
import { DebounceInput } from 'react-debounce-input';
import {
  JobSearchContext,
  JobSearchParamsType,
} from '../../context/JobSearchContext';
import ZipInput from '../common/inputs/ZipInput';
import PositionInput from '../common/inputs/PositionInput';

const radiusOptions = [
  { label: '200 Miles', value: 200 },
  { label: '100 Miles', value: 100 },
  { label: '50 Miles', value: 50 },
  { label: '40 Miles', value: 40 },
  { label: '30 miles', value: 30 },
  { label: '20 miles', value: 20 },
  { label: '10 miles', value: 10 },
];

const typeOptions = [
  { label: 'Perm or Temp', value: 'ANY' },
  { label: 'Perm only', value: 'PERM' },
  { label: 'Temp only', value: 'PLANNED' },
];

const daysOldOptions = [
  { label: 'Last 60 Days', value: 60 },
  { label: 'Last 14 Days', value: 14 },
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 3 Days', value: 3 },
];

export const defaultJobSearchParams: Partial<JobSearchParamsType> = {
  type: 'ANY',
  radius: '101',
  startDate: '',
  endDate: '',
  'days-old': '',
  keyword: '',
  evaluation: '',
};

const JobSearchForm = (props: {
  toggleFilters?: () => void;
  activeCategory: 'NATIONAL' | 'EXTERNAL' | 'SEARCH';
}) => {
  const { jobSearchParams, updateJobSearchParams, searchErrorMessage } =
    useContext(JobSearchContext);
  const [zip, setZip] = useState('');

  useEffect(() => {
    if (!zip && !!jobSearchParams?.zip) {
      setZip(jobSearchParams.zip);
    }
  }, [jobSearchParams]);

  useEffect(() => {
    if (zip.length >= 5 && zip !== jobSearchParams?.zip) {
      updateJobSearchParams({ zip });
    }
  }, [zip]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    updateJobSearchParams({ ...jobSearchParams, zip });
    if (!!props.toggleFilters) {
      props.toggleFilters();
    }
  }

  if (!jobSearchParams) {
    return null;
  }

  return (
    <Form id="job-search-form" onSubmit={handleSubmit} method="POST">
      <h2 className="d-none d-lg-flex">
        {props.activeCategory === 'NATIONAL'
          ? 'Search National Jobs'
          : props.activeCategory === 'EXTERNAL'
            ? 'Search External Jobs'
            : 'Search Jobs Near You'}
      </h2>
      <hr />
      <FormGroup floating>
        <Input
          tag={DebounceInput}
          element={'input'}
          minLength={2}
          debounceTimeout={300}
          type="text"
          name="keyword"
          id="keyword"
          value={jobSearchParams.keyword}
          onChange={(e) => updateJobSearchParams({ keyword: e.target.value })}
          valid={!!jobSearchParams?.keyword}
        />
        <Label for="keyword">Keyword (Find Anything)</Label>
      </FormGroup>
      <PositionInput
        position={jobSearchParams.position || ''}
        updatePosition={(newPosition) =>
          updateJobSearchParams({
            position: newPosition,
          })
        }
        required
        disabled={false}
      />
      {props.activeCategory !== 'NATIONAL' && (
        <>
          <ZipInput
            zip={zip}
            updateZip={(newZip) => {
              setZip(newZip);
            }}
            required
            disabled={false}
          />
          <FormGroup floating>
            <Input
              type="select"
              name="radius"
              id="radius"
              value={jobSearchParams.radius}
              onChange={(e) =>
                updateJobSearchParams({ radius: e.target.value })
              }
              valid={jobSearchParams?.radius !== '101'}
            >
              {radiusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            <Label for="radius">Distance</Label>
          </FormGroup>
        </>
      )}
      {props.activeCategory !== 'EXTERNAL' && (
        <>
          <FormGroup floating>
            <Input
              type="select"
              name="type"
              testid="type"
              value={jobSearchParams.type}
              onChange={(e) => updateJobSearchParams({ type: e.target.value })}
              valid={jobSearchParams?.type !== 'ANY'}
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            <Label for="type">Type</Label>
          </FormGroup>
          {!!jobSearchParams && jobSearchParams.type !== 'PERM' && (
            <>
              <FormGroup floating>
                <Input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={jobSearchParams?.startDate}
                  onChange={(e) =>
                    updateJobSearchParams({ startDate: e.target.value })
                  }
                  valid={!!jobSearchParams?.startDate}
                />
                <Label for="startDate">Temp Start Date</Label>
              </FormGroup>
              <FormGroup floating>
                <Input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={jobSearchParams?.endDate}
                  onChange={(e) =>
                    updateJobSearchParams({ endDate: e.target.value })
                  }
                  invalid={dayjs(jobSearchParams?.endDate).isBefore(
                    dayjs(jobSearchParams?.startDate)
                  )}
                  valid={
                    !!jobSearchParams?.endDate &&
                    !dayjs(jobSearchParams?.endDate).isBefore(
                      dayjs(jobSearchParams?.startDate)
                    )
                  }
                />
                <Label for="endDate">Temp End Date</Label>
                <FormFeedback>End date must come after start date</FormFeedback>
              </FormGroup>
            </>
          )}
        </>
      )}
      <FormGroup floating>
        <Input
          type="select"
          name="daysOld"
          id="daysOld"
          value={jobSearchParams?.['days-old']}
          onChange={(e) =>
            updateJobSearchParams({ 'days-old': e.target.value })
          }
          valid={!!jobSearchParams?.['days-old']}
        >
          <option key="default" value={''}>
            Any Date
          </option>
          {daysOldOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
        <Label for="daysOld">Posted Within</Label>
      </FormGroup>
      {props.activeCategory !== 'EXTERNAL' && (
        <FormGroup floating>
          <Input
            type="select"
            name="evaluation"
            id="evaluation"
            value={jobSearchParams?.evaluation}
            onChange={(e) =>
              updateJobSearchParams({ evaluation: e.target.value })
            }
            valid={!!jobSearchParams?.evaluation}
          >
            <option value={''}>All Jobs</option>
            <option value={'LIKED'}>Liked Jobs</option>
            <option value={'DISLIKED'}>Disliked Jobs</option>
          </Input>
          <Label for="evaluation">Evaluation</Label>
        </FormGroup>
      )}
      <Button
        form="job-search-form"
        type="submit"
        color="primary"
        className="mb-3 w-100 d-lg-none"
        style={{ minWidth: '150px' }}
      >
        Search
      </Button>
      <Alert
        color="danger"
        isOpen={!!searchErrorMessage}
        className="mx-3"
        fade={false}
      >
        {searchErrorMessage}
      </Alert>
    </Form>
  );
};

export default JobSearchForm;
